import { ChangeEvent, FC, MouseEvent, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks/ReduxHooks";
import { useNavigate } from "react-router-dom";
import DataField from "../../UI/data-field/DataField";
import { toastError } from "../../../utils/toast";
import { setResult } from "../../../redux/requests/IdentificationRequests";
import OrganizationSelector from "../../UI/organization-selector/OrganizationSelector";

interface IDataViewProps {}

const DataView: FC<IDataViewProps> = () => {
  const client = useAppSelector((state) => state.user.client);
  const navigate = useNavigate();
  const operator = useAppSelector((state) => state.user.operator);
  const legalEntity = localStorage.getItem("legal_entity");
  const clientOrganization = useAppSelector(
    (state) => state.user.clientOrganization,
  );

  const [isCorrect, setIsCorrect] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!client) navigate("/inn-check");
  }, [client]);

  const onIsCorrectChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsCorrect(event.target.checked);
  };

  const onIdentificationClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    navigate("/identification");
  };

  const onVerificationClick = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!client) return;
    if (
      !client.back_side_passport ||
      !client.date_of_birth ||
      !client.exp_date ||
      !client.address ||
      !client.inn ||
      !client.issue_date ||
      !client.phone ||
      !client.front_side_passport ||
      !client.national_id ||
      !client.serial_passport
    ) {
      toastError(
        "Данные указаны неверно или отсутствуют. Пожалуйста, пройдите идентификацию",
      );
      return;
    }
    if (!operator) return;

    if (legalEntity === "true" && !clientOrganization) {
      toastError("Выберите организацию клиента!");
      return;
    }

    if (operator.id === 91) {
      const token = await dispatch(
        setResult({
          address: client!.address,
          blood_group: client!.blood_group,
          date_of_birth: client!.date_of_birth,
          doc_number: client!.serial_passport,
          expiry_date: client!.exp_date,
          first_name: client!.name,
          first_name_en: client!.first_name_en,
          inn: client!.inn,
          issue_date: client!.issue_date,
          issuin_agency: client!.place,
          issuin_agency_en: client!.issuin_agency_en,
          last_name: client!.lastname,
          last_name_en: client!.last_name_en,
          middle_name: client!.middlename,
          middle_name_en: client!.middle_name_en,
          national_id: client!.national_id,
          nationality_code: client!.nationality_code,
          nationality_code_en: client!.nationality_code_en,
          phone: client!.phone,
          sex: client!.sex,
          legal_entity: legalEntity === "true",
          organization: clientOrganization,
        }),
      );

      if (!token) return;
      console.log("token", token);
      window.opener.postMessage(token, "*");
    } else {
      navigate("/verification");
    }
  };

  return (
    <div>
      <h1 className="font-bold text-2xl mb-10">Данные пользователя</h1>
      <div className="grid grid-cols-4 gap-4">
        <DataField label="Фамилия" value={client!.lastname} />
        <DataField label="Имя" value={client!.name} />
        <DataField label="Отчество" value={client!.middlename} />
        <DataField label="Пол" value={client!.sex} />

        <DataField label="Фамилия на английском" value={client!.last_name_en} />
        <DataField label="Имя на английском" value={client!.first_name_en} />
        <DataField
          label="Отчество на английском"
          value={client!.middle_name_en}
        />
        <DataField label="Группа крови" value={client!.blood_group} />
        <div className="col-span-4 grid grid-cols-4 gap-4">
          <DataField label="Дата рождения" value={client!.date_of_birth} />
          <DataField label="Дата выдачи" value={client!.issue_date} />
          <DataField label="Срок действия" value={client!.exp_date} />
          <DataField label="Номер паспорта" value={client!.serial_passport} />
        </div>
        <DataField label="Единый нац.номер" value={client!.national_id} />
        <DataField label="инн" value={client!.inn} />
        <DataField label="Номера телефона" value={client!.phone} />
        <div className="grid col-span-4 grid-cols-2 gap-4">
          <DataField label="Кем выдан" value={client!.place} />
          <DataField
            label="Кем выдан на английском"
            value={client!.issuin_agency_en}
          />
        </div>
        <div className="grid col-span-4 grid-cols-1 gap-4">
          <DataField label="Адрес" value={client!.address} />
        </div>

        <div className="grid col-span-4 grid-cols-1 gap-4">
          {client?.organization && (
            <OrganizationSelector organizations={client?.organization} />
          )}
        </div>
        <img className="w-[300px]" src={client?.front_side_passport} />
        <img className="w-[300px]" src={client?.back_side_passport} />

        {!operator!.username && (
          <div className="col-span-4 flex items-center mb-4">
            <input
              id="default-checkbox"
              type="checkbox"
              checked={isCorrect}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm"
              onChange={onIsCorrectChange}
            />
            <label
              htmlFor="default-checkbox"
              className="ms-2 text-sm font-medium"
            >
              Настоящим подтверждаю, что данные клиента проверены и являются
              достоверными
            </label>
          </div>
        )}
        <div className="col-span-4 flex">
          <button
            className="text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 focus:outline-none w-full disabled:cursor-not-allowed disabled:bg-gray-800 disabled:opacity-85"
            onClick={onIdentificationClick}
            disabled={isCorrect}
          >
            Пройти Идентификацию
          </button>
          {!operator!.username && (
            <button
              className="text-white bg-blue-700 hover:bg-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 focus:outline-none w-full disabled:cursor-not-allowed disabled:bg-gray-800 disabled:opacity-85"
              onClick={onVerificationClick}
              disabled={!isCorrect}
            >
              Пройти Верификацию
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataView;
